// Schema shared by the browser editor and the realtime server, so the HTML the
// server renders for ActionText is exactly what the editor produced.
import { Node } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import TaskList from "@tiptap/extension-task-list"
import TaskItem from "@tiptap/extension-task-item"

const ATTACHMENT_ATTRIBUTES = [
  "sgid", "content-type", "url", "filename", "filesize",
  "width", "height", "previewable", "caption", "presentation"
]

const camelCase = (name) => name.replace(/-(\w)/g, (_, letter) => letter.toUpperCase())

// <action-text-attachment sgid="…"> is how ActionText stores uploads. Rails
// re-derives everything but the sgid from the blob, so the rest is display data.
export const ActionTextAttachment = Node.create({
  name: "actionTextAttachment",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return Object.fromEntries(ATTACHMENT_ATTRIBUTES.map((name) => [
      camelCase(name),
      {
        default: null,
        parseHTML: (element) => element.getAttribute(name),
        renderHTML: (attributes) => {
          const value = attributes[camelCase(name)]
          return value == null ? {} : { [name]: value }
        }
      }
    ]))
  },

  parseHTML() {
    return [{ tag: "action-text-attachment" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["action-text-attachment", HTMLAttributes]
  }
})

const HEX_COLOR = /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i

// Links carry an optional colour, stored as an inline style so it survives in
// the ActionText HTML. Only plain hex colours are accepted.
export const ColoredLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      color: {
        default: null,
        parseHTML: (element) => {
          const color = element.getAttribute("style")?.match(/(?:^|;)\s*color:\s*(#[0-9a-f]{3,6})\s*(?:;|$)/i)?.[1]
          return color && HEX_COLOR.test(color) ? color : null
        },
        renderHTML: (attributes) => (HEX_COLOR.test(attributes.color || "") ? { style: `color: ${attributes.color}` } : {})
      }
    }
  }
})

export function richTextExtensions({ collaborative = false } = {}) {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      link: false,
      // Yjs keeps per-user undo history when collaborating.
      undoRedo: collaborative ? false : {}
    }),
    ColoredLink.configure({ openOnClick: false, autolink: true }),
    TaskList,
    TaskItem.configure({ nested: true }),
    ActionTextAttachment
  ]
}
